import React, {useEffect, useMemo} from 'react';
import {useNavigate} from 'react-router-dom';
import {X, Minus, Plus, Trash2, ShoppingCart, Sparkles, Star} from 'lucide-react';
import {useCart, lineCharge, memberSavingsPct, shouldShowMembershipJoinOffers} from '../context/CartContext';
import {getCartRecurringCadence, type CartLine} from '../types';
import Button from '@/shared/components/Button';
import MembershipNudge from './MembershipNudge';
import mylabsPlusLogo from '@/assets/dashboard/mylabs_plus_logo.png';
import {useMembershipPlans} from '@/modules/patient-dashboard/hooks/useMembershipPlans';
import {membershipLineKey} from '../types';

function formatDollars(cents: number): string {
    return (cents / 100).toFixed(2);
}

/**
 * Synthetic kit/curated lines render with an icon instead of a real
 * image. Sparkles for the bundle, Star for curated programs — keeps
 * the drawer visually distinct from straight product purchases.
 */
function LineThumbnail({line, size}: {line: CartLine; size: 'sm' | 'lg'}) {
    const dim = size === 'sm' ? 'w-16 h-16' : 'w-20 h-20';
    if (line.image_url) {
        return (
            <div className={`${dim} rounded-lg bg-mld-menu-background flex-shrink-0 overflow-hidden`}>
                <img src={line.image_url} alt={line.name} className="w-full h-full object-contain p-1" />
            </div>
        );
    }
    if (line.kind === 'custom_kit') {
        return (
            <div className={`${dim} rounded-lg bg-mld-primary/10 flex-shrink-0 flex items-center justify-center`}>
                <Sparkles className="h-5 w-5 text-mld-primary" />
            </div>
        );
    }
    if (line.kind === 'curated_rec') {
        return (
            <div className={`${dim} rounded-lg bg-mld-secondary/15 flex-shrink-0 flex items-center justify-center`}>
                <Star className="h-5 w-5 text-mld-secondary" />
            </div>
        );
    }
    return (
        <div className={`${dim} rounded-lg bg-mld-menu-background flex-shrink-0 flex items-center justify-center`}>
            <ShoppingCart className="h-4 w-4 text-mld-muted-text/60" />
        </div>
    );
}

function MembershipOfferCard({
    savingsPct,
    onExplore,
    onJoin,
}: {
    savingsPct: number;
    onExplore: () => void;
    onJoin: () => void;
}) {
    const membershipPlans = useMembershipPlans();
    const monthlyPlan = membershipPlans.find((plan) => plan.id === 'monthly') ?? membershipPlans[0];
    const monthlyPrice = `$${(monthlyPlan.priceCents / 100).toFixed(2)}/mo`;

    return (
        <div className="rounded-[22px] bg-[#d3f8fc] px-6 py-7 text-mld-plus-ink">
            <div className="flex items-center justify-between gap-5">
                <img src={mylabsPlusLogo} alt="My Labs Plus" className="h-auto w-[132px] shrink-0" />
                <div className="min-w-0 text-right">
                    <p className="text-[13px] leading-[1.35]">
                        {savingsPct > 0 ? (
                            <>
                                <span className="font-semibold">Save {savingsPct}%</span> on this order
                                <br />
                                and unlock Plus benefits
                            </>
                        ) : (
                            <>
                                Unlock more value
                                <br />
                                with Plus benefits
                            </>
                        )}
                    </p>
                    <p className="mt-2 text-[20px] leading-none">{monthlyPrice}</p>
                </div>
            </div>

            <div className="mt-6 grid grid-cols-2 gap-4">
                <button
                    type="button"
                    onClick={onExplore}
                    className="h-9 rounded-full border border-mld-plus-ink bg-transparent px-3 text-[12px] font-medium transition hover:bg-white/35 focus:outline-none focus:ring-2 focus:ring-mld-plus-ink/20">
                    Explore Benefits
                </button>
                <button
                    type="button"
                    onClick={onJoin}
                    className="h-9 rounded-full bg-[#78dff0] px-3 text-[12px] font-medium transition hover:brightness-[0.97] focus:outline-none focus:ring-2 focus:ring-mld-plus-ink/20">
                    Add to cart
                </button>
            </div>
        </div>
    );
}

const CartDrawer: React.FC = () => {
    const {
        items,
        removeItem,
        updateQuantity,
        subtotalCents,
        itemCount,
        hasRecurring,
        isDrawerOpen,
        closeDrawer,
        isMember,
        usesMemberPricing,
        addLine,
    } = useCart();
    const navigate = useNavigate();
    const savingsPct = useMemo(() => memberSavingsPct(items), [items]);
    const recurringCadence = useMemo(() => getCartRecurringCadence(items), [items]);
    const hasMembershipInCart = items.some(({line}) => line.kind === 'membership');
    const showMembershipJoinOffers = shouldShowMembershipJoinOffers(isMember) && !hasMembershipInCart;

    useEffect(() => {
        const handleEsc = (e: KeyboardEvent) => {
            if (e.key === 'Escape') closeDrawer();
        };
        if (isDrawerOpen) {
            document.addEventListener('keydown', handleEsc);
            document.body.style.overflow = 'hidden';
        }
        return () => {
            document.removeEventListener('keydown', handleEsc);
            document.body.style.overflow = '';
        };
    }, [isDrawerOpen, closeDrawer]);

    const membershipPlans = useMembershipPlans();

    const handleCheckout = () => {
        closeDrawer();
        navigate('/shop/checkout');
    };

    const handleViewCart = () => {
        closeDrawer();
        navigate('/shop/cart');
    };

    const handleExploreMembership = () => {
        closeDrawer();
        navigate('/membership/benefits');
    };

    const handleAddMembership = () => {
        const monthlyPlan = membershipPlans.find((plan) => plan.id === 'monthly') ?? membershipPlans[0];
        addLine({
            kind: 'membership',
            key: membershipLineKey('monthly'),
            plan: 'monthly',
            name: 'My Labs Plus — Monthly Membership',
            description: 'Exclusive member pricing, customized follow-up testing, and free shipping.',
            image_url: mylabsPlusLogo,
            price_cents: monthlyPlan.priceCents,
            member_price_cents: null,
            recurring: 'monthly',
        });
    };

    return (
        <>
            {/* Backdrop */}
            <div
                className={`fixed inset-0 bg-black/40 z-40 transition-opacity duration-300 ${
                    isDrawerOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
                }`}
                onClick={closeDrawer}
            />

            {/* Drawer panel */}
            <div
                className={`fixed top-0 right-0 h-full w-full max-w-md bg-mld-menu-background z-50 shadow-2xl flex flex-col transition-transform duration-300 ease-in-out ${
                    isDrawerOpen ? 'translate-x-0' : 'translate-x-full'
                }`}>
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-mld-panel-border">
                    <h2 className="text-lg font-bold text-mld-text-primary">My Cart</h2>
                    <button
                        type="button"
                        onClick={closeDrawer}
                        className="text-mld-muted-text hover:text-mld-text-secondary transition-colors"
                        aria-label="Close cart">
                        <X className="h-6 w-6" />
                    </button>
                </div>

                {/* Cart items */}
                <div className="flex-1 overflow-y-auto px-6 py-4">
                    {items.length === 0 ? (
                        <div className="text-center py-16">
                            <ShoppingCart className="h-12 w-12 text-mld-muted-text/60 mx-auto mb-3" />
                            <p className="text-mld-muted-text font-medium">Your cart is empty</p>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            {items.map(({line, quantity}) => {
                                const charge = lineCharge(line, usesMemberPricing);
                                const isSynthetic = line.kind !== 'product';
                                const showsMemberPrice = usesMemberPricing && line.member_price_cents != null;
                                return (
                                    <div key={line.key} className="flex gap-3">
                                        <LineThumbnail line={line} size="sm" />

                                        {/* Details */}
                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm font-medium text-mld-text-primary truncate">
                                                {line.name}
                                            </p>
                                            <p className="text-sm text-mld-muted-text">
                                                ${formatDollars(charge)}
                                                {line.recurring && (
                                                    <span className="text-xs">
                                                        {line.recurring === 'monthly' ? ' /mo' : ' /yr'}
                                                    </span>
                                                )}
                                                {showsMemberPrice && (
                                                    <span className="ml-1.5 text-[10px] font-medium text-mld-primary">
                                                        Member price
                                                    </span>
                                                )}
                                            </p>

                                            {/* Quantity + remove */}
                                            <div className="flex items-center gap-2 mt-1">
                                                {isSynthetic ? (
                                                    <span className="text-[10px] uppercase tracking-wide text-mld-text-secondary">
                                                        {line.kind === 'custom_kit'
                                                            ? line.mode === 'member'
                                                                ? 'Membership'
                                                                : 'One-time kit'
                                                            : line.kind === 'membership'
                                                              ? 'My Labs+ membership'
                                                              : 'Program'}
                                                    </span>
                                                ) : (
                                                    <div className="flex items-center border border-mld-panel-border rounded">
                                                        <button
                                                            type="button"
                                                            onClick={() => updateQuantity(line.key, quantity - 1)}
                                                            disabled={quantity <= 1}
                                                            className="px-1.5 py-0.5 text-mld-muted-text hover:text-mld-text-primary disabled:opacity-30">
                                                            <Minus className="h-3 w-3" />
                                                        </button>
                                                        <span className="px-2 text-xs font-medium text-mld-text-primary">
                                                            {quantity}
                                                        </span>
                                                        <button
                                                            type="button"
                                                            onClick={() => updateQuantity(line.key, quantity + 1)}
                                                            className="px-1.5 py-0.5 text-mld-muted-text hover:text-mld-text-primary">
                                                            <Plus className="h-3 w-3" />
                                                        </button>
                                                    </div>
                                                )}
                                                <button
                                                    type="button"
                                                    onClick={() => removeItem(line.key)}
                                                    className="text-mld-muted-text hover:text-mld-danger transition-colors"
                                                    aria-label="Remove">
                                                    <Trash2 className="h-3.5 w-3.5" />
                                                </button>
                                            </div>
                                        </div>

                                        {/* Line total */}
                                        <p className="text-sm font-medium text-mld-text-primary flex-shrink-0">
                                            ${formatDollars(charge * quantity)}
                                        </p>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>

                {/* Footer — membership offer, totals + buttons */}
                {items.length > 0 && (
                    <>
                        {showMembershipJoinOffers && (
                            <div className="px-5 pb-5">
                                <MembershipOfferCard
                                    savingsPct={savingsPct}
                                    onExplore={handleExploreMembership}
                                    onJoin={handleAddMembership}
                                />
                            </div>
                        )}
                        <div className="border-t border-mld-panel-border px-6 py-4">
                            {showMembershipJoinOffers && (
                                <div className="mb-3">
                                    <MembershipNudge savingsPct={savingsPct} />
                                </div>
                            )}
                            <div className="space-y-1 mb-4">
                                <div className="flex justify-between text-sm">
                                    <span className="text-mld-muted-text">Subtotal</span>
                                    <span className="font-medium text-mld-text-primary">
                                        ${formatDollars(subtotalCents)}
                                    </span>
                                </div>
                                <div className="flex justify-between text-sm">
                                    <span className="text-mld-muted-text">Shipping</span>
                                    <span className="text-mld-muted-text">Free</span>
                                </div>
                                <div className="flex justify-between text-sm">
                                    <span className="text-mld-muted-text">Tax</span>
                                    <span className="text-mld-muted-text">Calculated at checkout</span>
                                </div>
                            </div>
                            <div className="flex justify-between text-lg font-bold text-mld-text-primary mb-2">
                                <span>Total</span>
                                <span>${formatDollars(subtotalCents)}</span>
                            </div>
                            {hasRecurring && recurringCadence && (
                                <p className="mb-4 text-[11px] text-mld-text-secondary">
                                    {recurringCadence === 'mixed'
                                        ? 'Includes recurring subscriptions. You’ll be charged today and again according to each item’s billing schedule.'
                                        : `Includes ${recurringCadence === 'annual' ? 'an annual' : 'a monthly'} subscription. You’ll be charged today and on the same date each ${
                                              recurringCadence === 'annual' ? 'year' : 'month'
                                          } thereafter.`}
                                </p>
                            )}

                            <Button
                                variant="primary"
                                size="lg"
                                fullWidth
                                onClick={handleCheckout}
                                className="rounded-full mb-2">
                                Checkout
                            </Button>
                            <Button variant="ghost" size="md" fullWidth onClick={handleViewCart}>
                                View full cart ({itemCount} items)
                            </Button>
                        </div>
                    </>
                )}
            </div>
        </>
    );
};

export default CartDrawer;
